import { environment } from "@/environments/environment";

import type {
  ChatStatus,
  ChatStatusCountsResponse
} from "@/features/chat/domain/repositories/chat";
import type {
  GetMessagesParams,
  SendMessageParams
} from "@/features/chat/domain/repositories/message";
import { chatDataSourceImplementation } from "@/features/chat/infrastructure/datasources/chat/chatDataSourceImplementation";
import { messagingDataSourceImplementation } from "@/features/chat/infrastructure/datasources/messaging/messagingDataSourceImplementation";
import { httpImplementation } from "@/shared/services/http";
import {
  getMockUserData,
  mockChats,
  mockStatusCounts
} from "@/features/chat/shared/mocks";
import type {
  Chat,
  ChatListResponse,
  ChatMessage,
  ChatMessagesResponse
} from "@/features/chat/shared/types";

const simulateAsync = <T>(value: T, timeout: number) =>
  new Promise<T>((resolve) => setTimeout(() => resolve(value), timeout));

export const USE_STUB = environment.useChatStub;

// Initialize datasources for real API calls
const gateway = httpImplementation({
  baseURL: environment.styxApiUrl,
  withInterceptor: true
});
const chatDataSource = chatDataSourceImplementation(gateway);
const messagingDataSource = messagingDataSourceImplementation(gateway);
const SIMULATED_DELAY = 2000;
const SEND_MESSAGE_DELAY = 3000; // 3 seconds to see the sending state
const PAGE_SIZE = 10;

export const getChats = async (
  cursor?: string | null,
  limit: number = PAGE_SIZE,
  query?: string | null,
  status?: ChatStatus | null,
  lastMessageOpts?: "full" | "light"
): Promise<ChatListResponse> => {
  if (!USE_STUB) {
    return await chatDataSource.getChats({
      cursor: cursor ?? undefined,
      limit,
      query: query ?? undefined,
      status: status ?? undefined,
      last_message_opts: lastMessageOpts
    });
  }

  await new Promise((resolve) => setTimeout(resolve, SIMULATED_DELAY));

  // Filter by query if provided
  let filteredChats = mockChats;
  if (query && query.trim()) {
    filteredChats = mockChats.filter((chat) =>
      chat.user.username.toLowerCase().includes(query.toLowerCase())
    );
  }
  // To Do optimize this filter by status
  if (status) {
    if (status === "unread") {
      filteredChats = filteredChats.filter((chat) => !chat.is_read);
    } else if (status === "to_deliver") {
      filteredChats = filteredChats.filter(
        (chat) => chat.to_deliver != null && chat.to_deliver > 0
      );
    } else if (status === "pending_request") {
      filteredChats = filteredChats.filter(
        (chat) => chat.media_request != null && chat.media_request > 0
      );
    } else if (status === "blocked") {
      filteredChats = filteredChats.filter(
        (chat) => chat.user.is_blacklisted === true
      );
    } else if (status === "muted") {
      filteredChats = filteredChats.filter(
        (chat) => chat.user.is_muted === true
      );
    }
  }

  let startIndex = 0;
  if (cursor) {
    try {
      startIndex = parseInt(atob(cursor), 10);
    } catch {
      startIndex = 0;
    }
  }

  const endIndex = Math.min(startIndex + limit, filteredChats.length);
  const data = filteredChats.slice(startIndex, endIndex);
  const hasMore = endIndex < filteredChats.length;
  const nextCursor = hasMore ? btoa(endIndex.toString()) : undefined;

  return {
    data,
    cursor: nextCursor,
    count: filteredChats.length
  };
};

export const markChatAsRead = async (chatId: string): Promise<void> => {
  if (!USE_STUB) {
    await chatDataSource.markChatAsRead(chatId);
    return;
  }
  return simulateAsync(undefined, 200);
};

export const toggleChatMute = async (
  chatId: string,
  muted: boolean
): Promise<void> => {
  if (!USE_STUB) {
    if (muted) {
      await chatDataSource.muteNotificationsChat(chatId);
    } else {
      await chatDataSource.unmuteNotificationsChat(chatId);
    }
    return;
  }
  return simulateAsync(undefined, 200);
};

// Mocked status counts for list chats page
export const getStatusCounts = async (): Promise<ChatStatusCountsResponse> => {
  if (!USE_STUB) {
    return await chatDataSource.getStatusCounts<ChatStatusCountsResponse>();
  }

  await new Promise((resolve) => setTimeout(resolve, SIMULATED_DELAY));

  return mockStatusCounts;
};

export const getChatByUserId = async (userId: string): Promise<Chat> => {
  if (!USE_STUB) {
    return await chatDataSource.getChatByUserId<Chat>(userId);
  }

  await new Promise((resolve) => setTimeout(resolve, SIMULATED_DELAY));

  // Extract userId from chatId format "creatorId#fanId"
  const chat = mockChats.find((c) => {
    const [, fanId] = c.id.split("#");
    const matches = fanId === userId || c.id === userId;
    console.log(
      `[STUB] getChatByUserId - Looking for userId: ${userId}, chatId: ${c.id}, fanId: ${fanId}, matches: ${matches}`
    );
    return matches;
  });

  if (!chat) {
    console.error(
      `[STUB] Chat not found for userId: ${userId}. Available chats:`,
      mockChats.map((c) => {
        const [, fanId] = c.id.split("#");
        return { id: c.id, fanId, username: c.user?.username };
      })
    );
    throw new Error(`Chat not found for userId: ${userId}`);
  }

  console.log(`[STUB] Found chat for userId ${userId}:`, chat);
  return chat;
};

export const getMessages = async (
  params: GetMessagesParams
): Promise<ChatMessagesResponse> => {
  if (!USE_STUB) {
    return await messagingDataSource.getMessages<ChatMessagesResponse>(params);
  }

  await new Promise((resolve) => setTimeout(resolve, SIMULATED_DELAY));

  const chat = mockChats.find((c) => {
    const [, participantId] = c.id.split("#");
    return participantId === params.userId || c.id === params.userId;
  });

  const senderId = chat
    ? chat.id.split("#")[0]
    : "654ff48d-23f4-4e2c-8269-b7da00857735";

  const mockMessages: ChatMessagesResponse = {
    data: {
      id: `${senderId}#${params.userId}`,
      messages: [
        {
          id: "msg-1",
          sender_id: params.userId,
          receiver_id: senderId,
          date: new Date(Date.now() - 3600000).toISOString(),
          type: "text",
          content: "Hello! How are you?",
          status: "active",
          read_at: new Date(Date.now() - 3000000).toISOString()
        },
        {
          id: "msg-2",
          sender_id: senderId,
          receiver_id: params.userId,
          date: new Date(Date.now() - 1800000).toISOString(),
          type: "text",
          content: "I'm doing great, thanks for asking!",
          status: "active",
          read_at: null
        },
        {
          id: "msg-3",
          sender_id: params.userId,
          receiver_id: senderId,
          date: new Date(Date.now() - 900000).toISOString(),
          type: "private_media",
          content: "Check this out!",
          status: "active",
          read_at: null
        },
        {
          id: "msg-4",
          sender_id: params.userId,
          receiver_id: senderId,
          date: new Date(Date.now() - 600000).toISOString(),
          type: "tip",
          content: "Un pourboire de 10 EUR a été envoyé avec succès !",
          status: "active",
          read_at: null
        },
        {
          id: "msg-5",
          sender_id: senderId,
          receiver_id: params.userId,
          date: new Date(Date.now() - 300000).toISOString(),
          type: "live",
          content: "",
          status: "active",
          read_at: null,
          live: {
            booking_id: "booking-123-pending",
            price: 2299,
            currency: "EUR",
            duration: 15,
            offer_status: "pending_acceptance"
          }
        },
        {
          id: "msg-6",
          sender_id: senderId,
          receiver_id: params.userId,
          date: new Date(Date.now() - 200000).toISOString(),
          type: "live",
          content: "",
          status: "active",
          read_at: null,
          live: {
            booking_id: "booking-456-paid",
            price: 2299,
            currency: "EUR",
            duration: 15,
            offer_status: "paid"
          }
        }
      ]
    },
    count: 3,
    cursor: params.cursor ? null : "next-cursor"
  };

  return mockMessages;
};

export const sendMessage = async (
  params: SendMessageParams
): Promise<ChatMessage> => {
  if (!USE_STUB) {
    return await messagingDataSource.sendMessage<ChatMessage>(params);
  }

  await new Promise((resolve) => setTimeout(resolve, SEND_MESSAGE_DELAY));

  // Create a mock message response
  const mockMessage: ChatMessage = {
    id: `msg-${Date.now()}`,
    sender_id: params.receiverId,
    receiver_id: params.receiverId,
    date: new Date().toISOString(),
    type: params.type ?? "text",
    content: params.content,
    status: "active",
    read_at: null
  };

  return mockMessage;
};

export const getFan = async (userId: string) => {
  if (!USE_STUB) {
    return await chatDataSource.getUserById(userId);
  }

  await new Promise((resolve) => setTimeout(resolve, SIMULATED_DELAY / 2));

  return getMockUserData(userId);
};
